import { DataTypeType, FieldType } from 'modules/domains';
import { useError } from 'react-use';
import { ApiMethod, get } from 'services/api';
import {
  RestQueryHookOptions,
  UseRestMutationOptions,
  useRestMutation,
  useRestQuery,
} from 'utils/hooks/api';

export type Mapping = {
  createdAt: string;
  dataType: DataTypeType;
  description?: string;
  fieldName: string;
  fieldType: FieldType;
  /** Property to help determine custom event state, along with isSaved. Both `undefined` and `true` should evaluate to `true`, `false === false` */
  isAllowed?: boolean;
  isCustomEvent?: boolean;
  isHidden?: boolean;
  /** Property to help determine custom event state, along with isAllowed. Both `undefined` and `true` should evaluate to `true`, `false === false` */
  isSaved?: boolean;
  isSystemField?: boolean;
  projectId: number;
  sampleValue?: string;
};

export type MappingVisibilityPayload = {
  projectId: number;
  fields: (string | number)[];
  dataType: DataTypeType;
};

type UpdateDescriptionPayload = {
  fieldName: string;
  dataType: DataTypeType;
  description: string;
  projectId: number;
};

type UpdateSampleValuePayload = {
  fieldName: string;
  dataType: DataTypeType;
  sampleValue: string;
  projectId: number;
};

export type EventStatePayload = {
  projectId: number;
  events: string[];
};

export const MAPPINGS_URL = '/mappings';
export const HIDE_FIELDS_URL = '/mappings/hideFields';
export const UNHIDE_FIELDS_URL = '/mappings/unhideFields';
export const UPDATE_DESCRIPTION_URL = '/mappings/updateDescription';
export const UPDATE_SAMPLE_VALUE_URL = '/mappings/updateSampleValue';

export const getMappings = async (dataType?: DataTypeType) => {
  const data = await get<Mapping[]>(MAPPINGS_URL, { params: { dataType } });
  return data.filter((mapping) => !mapping.isHidden);
};

/**
 * Generic mappings query
 */
export const useMappingQuery = (
  params?: { dataType?: DataTypeType; fetchLatest?: boolean; filterOutHiddenFields?: boolean },
  queryOptions?: RestQueryHookOptions,
) => useRestQuery<Mapping[]>(MAPPINGS_URL, params, queryOptions);

export const useMappingsByDataType = (dataType?: DataTypeType) => {
  const onError = useError();

  const { data, ...result } = useRestQuery<Mapping[]>(MAPPINGS_URL, { dataType }, { onError });
  const filteredData = data?.filter((mapping) => !mapping.isHidden);
  return { data: filteredData, ...result };
};

export const useUpdateDescription = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, UpdateDescriptionPayload>(
    UPDATE_DESCRIPTION_URL,
    ApiMethod.post,
    options,
  );

export const useUpdateSampleValue = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, UpdateSampleValuePayload>(
    UPDATE_SAMPLE_VALUE_URL,
    ApiMethod.post,
    options,
  );

/**
 * Hide fields mutation
 */
export const useHideFields = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, MappingVisibilityPayload>(HIDE_FIELDS_URL, ApiMethod.post, options);

/**
 * Show fields mutation
 */
export const useUnhideFields = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, MappingVisibilityPayload>(UNHIDE_FIELDS_URL, ApiMethod.post, options);

/**
 * Custom events
 */
export const ALLOW_EVENTS_URL = '/mappings/allowEvents';
export const DISALLOW_EVENTS_URL = '/mappings/disallowEvents';
export const CREATE_CUSTOM_EVENT_URL = '/mappings/createCustomEvent';
export const FETCH_CUSTOM_EVENT_MAPPINGS_URL = '/mappings/customEventMappings';
export const FETCH_CUSTOM_EVENTS_URL = '/mappings/customEvents';
export const CREATE_CUSTOM_EVENT_FIELD_URL = '/mappings/createCustomEventMapping';
export const NOT_SAVE_EVENTS_URL = '/mappings/notSaveEvents';
export const SAVE_EVENTS_URL = '/mappings/saveEvents';

export const useCreateCustomEventQuery = (options?: UseRestMutationOptions) =>
  useRestMutation(CREATE_CUSTOM_EVENT_URL, ApiMethod.post, options);

export const useCreateCustomEventFieldQuery = (
  customEventName: string,
  options: UseRestMutationOptions,
) => {
  const url = `${CREATE_CUSTOM_EVENT_FIELD_URL}/${customEventName}`;
  return useRestMutation(url, ApiMethod.post, options);
};

// Get all custom events
export const useCustomEventsQuery = (
  params?: { fetchLatest: boolean },
  queryOptions?: RestQueryHookOptions,
) => useRestQuery<Mapping[]>(FETCH_CUSTOM_EVENTS_URL, params, queryOptions);

// Get all custom events and their associated mappings
export const useCustomMappingsQuery = () =>
  useRestQuery<Mapping[]>(FETCH_CUSTOM_EVENT_MAPPINGS_URL);

// Get mappings for a single custom event (by name)
export const useCustomEventDetailsQuery = (name: string, queryOptions?: RestQueryHookOptions) =>
  useRestQuery(`${FETCH_CUSTOM_EVENTS_URL}/${name}`, undefined, queryOptions);

/** Mark event as saved */
export const useAllowEvents = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, EventStatePayload>(ALLOW_EVENTS_URL, ApiMethod.post, options);

/** Mark event as saved */
export const useDisallowEvents = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, EventStatePayload>(DISALLOW_EVENTS_URL, ApiMethod.post, options);

/** Mark event as saved */
export const useNotSaveEvents = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, EventStatePayload>(NOT_SAVE_EVENTS_URL, ApiMethod.post, options);

/** Mark event as saved */
export const useSaveEvents = (options?: UseRestMutationOptions) =>
  useRestMutation<unknown, EventStatePayload>(SAVE_EVENTS_URL, ApiMethod.post, options);

/**
 * Purchase field mappings
 */
export const CREATE_PURCHASE_MAPPINGS = '/mappings/createPurchaseMapping';
export const FETCH_PURCHASE_MAPPINGS_URL = '/mappings/purchaseMappings';

export const useCreatePurchaseMapping = (options?: UseRestMutationOptions) =>
  useRestMutation(CREATE_PURCHASE_MAPPINGS, ApiMethod.post, options);

export const usePurchaseMappingsQuery = (queryOptions?: RestQueryHookOptions) =>
  useRestQuery<Mapping[]>(FETCH_PURCHASE_MAPPINGS_URL, undefined, queryOptions);

/**
 * User mappings
 */
export const CREATE_USER_MAPPINGS = '/mappings/createUserMapping';
export const FETCH_USER_MAPPINGS_URL = '/mappings/userMappings';

export const useUserMappingsQuery = (queryOptions?: RestQueryHookOptions) =>
  useRestQuery<Mapping[]>(FETCH_USER_MAPPINGS_URL, undefined, queryOptions);

export const useCreateUserMapping = (options?: UseRestMutationOptions) =>
  useRestMutation(CREATE_USER_MAPPINGS, ApiMethod.post, options);
